import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { QUERY_KEYS } from "@/hooks/use-queries";
import { useToast } from "@/hooks/use-toast";
import {
  Wallet,
  CreditCard,
  Clock,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatCard, formatCents } from "./shared";
import { usePlatformStats } from "@/hooks/use-queries";

export function WalletsPlansDashboard({ wallets, paymentPlans }: { wallets: any[]; paymentPlans: any[] }) {
  const { toast } = useToast();
  const [walletPage, setWalletPage] = useState(1);
  const walletsPerPage = 20;
  const { data: platformStats, isLoading: statsLoading } = usePlatformStats();

  const { data: hardshipRequests, isLoading: hardshipLoading } = useQuery<any[]>({
    queryKey: ["/api/hardship-requests"],
  });

  const reviewMutation = useMutation({
    mutationFn: async ({ id, decision, note }: { id: string; decision: string; note: string }) => {
      return apiRequest("POST", `/api/hardship-requests/${id}/review`, { status: decision, reviewNotes: note });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/hardship-requests"] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.paymentPlans });
      toast({ title: "Request Reviewed", description: "Hardship request has been processed." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to review hardship request.", variant: "destructive" });
    },
  });

  const totalWallets = wallets.length;
  const activePlans = paymentPlans.filter((p: any) => p.status === "active").length;
  const completedPlans = paymentPlans.filter((p: any) => p.status === "completed").length;
  const pausedPlans = paymentPlans.filter((p: any) => p.status === "paused").length;
  const defaultedPlans = paymentPlans.filter((p: any) => p.status === "defaulted").length;
  const pendingHardship = hardshipRequests?.filter((h: any) => h.status === "pending").length || 0;
  // Use DB-aggregated total from platform-stats (authoritative single SQL SUM).
  // Fallback: client-side sum using correct field name `totalContributions`.
  const totalContributions = platformStats?.wallets?.totalContributions
    ?? wallets.reduce((sum: number, w: any) => sum + (w.totalContributions || 0), 0);

  return (
    <>
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard
          title="Total Wallets"
          value={totalWallets}
          description="All wallet accounts"
          icon={Wallet}
          color="blue"
        />
        <StatCard
          title="Active Payment Schedules"
          value={activePlans}
          description={`${paymentPlans.length} total plans`}
          icon={CreditCard}
          color="green"
        />
        <StatCard
          title="Pending Hardship Requests"
          value={pendingHardship}
          description="Awaiting admin review"
          icon={Clock}
          color="yellow"
        />
        <StatCard
          title="Total Contributions"
          value={statsLoading ? "—" : formatCents(totalContributions)}
          description="All-time wallet contributions"
          icon={DollarSign}
          color="purple"
        />
      </div>

      <Card data-testid="wallets-table-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5" />
            Wallet Overview
          </CardTitle>
          <CardDescription>{totalWallets} wallets registered</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto max-h-96">
            <Table data-testid="wallets-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Owner</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Balance</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {wallets.slice((walletPage - 1) * walletsPerPage, walletPage * walletsPerPage).map((wallet: any) => (
                  <TableRow key={wallet.id} data-testid={`wallet-row-${wallet.id}`}>
                    <TableCell className="font-medium">{wallet.ownerName || wallet.userId?.slice(0, 12) || "Unknown"}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{wallet.type || "individual"}</Badge>
                    </TableCell>
                    <TableCell className="font-mono">{formatCents(wallet.balance || 0)}</TableCell>
                    <TableCell>
                      <Badge variant={(wallet.balance || 0) > 0 ? "default" : "secondary"}>
                        {(wallet.balance || 0) > 0 ? "Active" : "Empty"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {wallets.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                      No wallets found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {wallets.length > walletsPerPage && (
            <div className="flex items-center justify-between gap-2 mt-4">
              <p className="text-sm text-muted-foreground">
                Showing {(walletPage - 1) * walletsPerPage + 1}-{Math.min(walletPage * walletsPerPage, wallets.length)} of {wallets.length} wallets
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={walletPage <= 1}
                  onClick={() => setWalletPage(p => Math.max(1, p - 1))}
                  data-testid="button-wallets-prev-page"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">Page {walletPage} of {Math.ceil(wallets.length / walletsPerPage)}</span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={walletPage >= Math.ceil(wallets.length / walletsPerPage)}
                  onClick={() => setWalletPage(p => Math.min(Math.ceil(wallets.length / walletsPerPage), p + 1))}
                  data-testid="button-wallets-next-page"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card data-testid="payment-plans-summary">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Payment Schedules by Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm">Active</span>
                <div className="flex items-center gap-2">
                  <Progress value={(activePlans / Math.max(paymentPlans.length, 1)) * 100} className="w-24" />
                  <span className="font-medium w-8 text-right">{activePlans}</span>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm">Completed</span>
                <div className="flex items-center gap-2">
                  <Progress value={(completedPlans / Math.max(paymentPlans.length, 1)) * 100} className="w-24" />
                  <span className="font-medium w-8 text-right">{completedPlans}</span>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm">Paused</span>
                <div className="flex items-center gap-2">
                  <Progress value={(pausedPlans / Math.max(paymentPlans.length, 1)) * 100} className="w-24" />
                  <span className="font-medium w-8 text-right">{pausedPlans}</span>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm">Defaulted</span>
                <div className="flex items-center gap-2">
                  <Progress value={(defaultedPlans / Math.max(paymentPlans.length, 1)) * 100} className="w-24" />
                  <span className="font-medium w-8 text-right">{defaultedPlans}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="hardship-requests-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              Pending Hardship Requests
            </CardTitle>
            <CardDescription>{pendingHardship} requests awaiting review</CardDescription>
          </CardHeader>
          <CardContent>
            {hardshipLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-16" />)}
              </div>
            ) : (
              <ScrollArea className="h-64">
                <div className="space-y-3">
                  {hardshipRequests?.filter((h: any) => h.status === "pending").map((request: any) => (
                    <div key={request.id} className="flex items-center justify-between gap-2 p-3 rounded-lg bg-muted/50" data-testid={`hardship-request-${request.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">Plan #{request.paymentPlanId?.slice(0, 8)}</p>
                        <p className="text-xs text-muted-foreground truncate">{request.reason || "No reason provided"}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => reviewMutation.mutate({ id: request.id, decision: "approved", note: "Approved by admin" })}
                          disabled={reviewMutation.isPending}
                          data-testid={`button-approve-hardship-${request.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => reviewMutation.mutate({ id: request.id, decision: "denied", note: "Denied by admin" })}
                          disabled={reviewMutation.isPending}
                          data-testid={`button-deny-hardship-${request.id}`}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Deny
                        </Button>
                      </div>
                    </div>
                  ))}
                  {(!hardshipRequests || hardshipRequests.filter((h: any) => h.status === "pending").length === 0) && (
                    <p className="text-sm text-muted-foreground text-center py-4">No pending hardship requests</p>
                  )}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
